/**
 * Gallery view + selection state.
 *
 * Selection is keyed by the stable original S3 key (a Set<string>), never by
 * array index — so sorting, filtering, and pagination can never re-point a
 * selection at the wrong file (the legacy bug).
 */
import { create } from 'zustand';

export type ViewMode = 'grid' | 'list';
export type SortBy = 'date' | 'name' | 'size';
export type MediaTypeFilter = 'all' | 'images' | 'videos';

export interface DateRange {
  from: string;
  to: string;
}

interface GalleryState {
  viewMode: ViewMode;
  gridColumns: number; // 1-6
  sortBy: SortBy;
  sortDescending: boolean;
  search: string;
  dateRange: DateRange;
  mediaType: MediaTypeFilter;
  /** Selected original keys. */
  selection: Set<string>;

  setViewMode: (mode: ViewMode) => void;
  setGridColumns: (cols: number) => void;
  setSortBy: (sort: SortBy) => void;
  toggleSortDirection: () => void;
  setSearch: (term: string) => void;
  setDateRange: (range: DateRange) => void;
  setMediaType: (type: MediaTypeFilter) => void;

  toggleSelected: (key: string) => void;
  selectMany: (keys: string[]) => void;
  isSelected: (key: string) => boolean;
  clearSelection: () => void;
}

export const useGalleryStore = create<GalleryState>((set, get) => ({
  viewMode: 'grid',
  gridColumns: 4,
  sortBy: 'date',
  // Ascending = oldest-first, which matches the S3 key fetch order. This keeps
  // infinite scroll append-only (new pages add at the bottom; nothing already
  // on screen reorders). Users can flip to newest-first via the sort toggle.
  sortDescending: false,
  search: '',
  dateRange: { from: '', to: '' },
  mediaType: 'all',
  selection: new Set<string>(),

  setViewMode: (viewMode) => set({ viewMode }),
  setGridColumns: (gridColumns) => set({ gridColumns: Math.min(6, Math.max(1, gridColumns)) }),
  setSortBy: (sortBy) => set({ sortBy }),
  toggleSortDirection: () => set((s) => ({ sortDescending: !s.sortDescending })),
  setSearch: (search) => set({ search }),
  setDateRange: (dateRange) => set({ dateRange }),
  setMediaType: (mediaType) => set({ mediaType }),

  toggleSelected: (key) =>
    set((s) => {
      const next = new Set(s.selection);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { selection: next };
    }),
  selectMany: (keys) => set({ selection: new Set(keys) }),
  isSelected: (key) => get().selection.has(key),
  clearSelection: () => set({ selection: new Set<string>() }),
}));
